import React, { useState } from 'react';

import { DataKey, DataValue, SmartOptionConfig, SmartOptionResource } from 'types';

import api from '../../api';
import _ from '../../locale';
import { styled } from '../../theme';
import CreateSmartOptionModal from '../CreateSmartOptionModal';
import {
    Button,
    DeleteResourceModal,
    FlexRow,
    ListItem,
    ListItemTop,
    Popover,
    SvgIcon,
} from '../index';

const SelectAllButtonWrapper = styled('div')({
    display: 'flex',
});

const StyledButton = styled(Button)({
    minWidth: 130,
});

const LeftSideButton = styled(Button)({
    minWidth: 90,
    borderRadius: '4px 0 0 4px',
    padding: '6px 0px',
});

const ChevronButton = styled(Button)({
    minWidth: 30,
    width: 30,
    borderRadius: '0 4px 4px 0',
    borderLeft: '0.5px solid rgb(240,242,241)',
});

const ChevronIcon = styled(SvgIcon)({
    height: 15,
});

const DeleteIcon = styled(SvgIcon)(({ theme }) => ({
    color: theme.color.danger1,
    ':hover, :focus': {
        color: theme.color.danger2,
    },
    ':active': {
        color: theme.color.danger3,
    },
}));

const CREATE_MODAL = 'create';
const DELETE_MODAL = 'delete';

type Props = {
    smartOptions: [];
    onChange: (value) => void;
    onCreate?: (resource: SmartOptionResource) => void;
    onDelete?: (id: number) => void;
    granularity: DataValue;
    isSelectAllEnabled: boolean;
    isCreatable: boolean;
    value?: DataValue;
    granularityKey?: DataKey;
};

const SmartOptionControl = ({
    smartOptions,
    onChange,
    granularity,
    isSelectAllEnabled = true,
    isCreatable = false,
    value,
    granularityKey,
    onCreate,
    onDelete,
}: Props) => {
    const [visibleModal, setVisibleModal] = useState<string | null>(null);
    const [smartOptionResource, setSmartOptionResource] =
        useState<Partial<SmartOptionResource> | null>(null);

    const handleSmartOptionSelect = (item) => {
        if (item.onSelect && granularityKey && value) {
            item.onSelect(granularityKey, value);
        } else {
            onChange(item.getNewSelectedValues());
        }
    };

    const handleSelectAllClick = (ev) => {
        onChange(granularity);
    };

    const handleCustomSmartOptionCreation = (granularityKey: DataKey, value: DataValue) => {
        setSmartOptionResource({
            // description: 'Created from Data collection editor',
            filter: granularityKey,
            filter_values: value,
        });

        setVisibleModal(CREATE_MODAL);
    };

    const handleModalClose = () => {
        setVisibleModal(null);
    };

    const handleDeleteClick = (item: SmartOptionConfig) => {
        setSmartOptionResource(item);
        setVisibleModal(DELETE_MODAL);
    };

    const options: SmartOptionConfig[] = [];

    if (isCreatable) {
        const createCustomSmartOption: SmartOptionConfig = {
            id: -1,
            label: _('smart_option_save_selection_label'),
            getNewSelectedValues: () => null,
            onSelect: handleCustomSmartOptionCreation,
            separator: true,
        };
        options.push(createCustomSmartOption);
    }

    options.push(...smartOptions);

    return (
        <>
            <Popover
                placement="bottom"
                renderContent={({ toggleVisibility }) => (
                    <div>
                        {options.map((item: SmartOptionConfig) => {
                            const DynamicComponent = item.separator ? ListItemTop : ListItem;
                            return (
                                <DynamicComponent
                                    key={item.id}
                                    isSelected={false}
                                    disabled={item.disabled}
                                    onClick={(ev) => {
                                        !item.disabled && toggleVisibility();
                                        handleSmartOptionSelect(item);
                                    }}
                                >
                                    <FlexRow justify="space-between" grow={1}>
                                        <div>{item.label}</div>
                                        {item.isDeletable && (
                                            <div>
                                                <DeleteIcon
                                                    size="large"
                                                    tooltip={_('tooltip_delete')}
                                                    src="/icons/ic-trash.svg"
                                                    onClick={() => handleDeleteClick(item)}
                                                />
                                            </div>
                                        )}
                                    </FlexRow>
                                </DynamicComponent>
                            );
                        })}
                    </div>
                )}
            >
                {({ ref, toggleVisibility, visibility }) => (
                    <SelectAllButtonWrapper>
                        {isSelectAllEnabled || isCreatable ? (
                            options && options.length ? (
                                <>
                                    <LeftSideButton onClick={handleSelectAllClick} color="primary">
                                        {_('select_all')}
                                    </LeftSideButton>
                                    <ChevronButton onClick={toggleVisibility} color="primary">
                                        <div ref={ref}>
                                            <ChevronIcon
                                                src={
                                                    visibility
                                                        ? '/icons/ic-arrow-up.svg'
                                                        : '/icons/ic-arrow-down.svg'
                                                }
                                                color={(theme) => theme.color.white}
                                            />
                                        </div>
                                    </ChevronButton>
                                </>
                            ) : (
                                <StyledButton onClick={handleSelectAllClick} color="primary">
                                    {_('select_all')}
                                </StyledButton>
                            )
                        ) : (
                            <StyledButton
                                ref={ref}
                                color="primary"
                                disabled={!(options && options.length)}
                                onClick={toggleVisibility}
                            >
                                {_('data_explorer_select_more')}
                            </StyledButton>
                        )}
                    </SelectAllButtonWrapper>
                )}
            </Popover>
            <CreateSmartOptionModal
                resource={smartOptionResource}
                isVisible={visibleModal === CREATE_MODAL}
                onClose={handleModalClose}
                onSubmit={(resource) => onCreate && onCreate(resource)}
            />
            <DeleteResourceModal
                title={_('smart_option_delete_title')}
                resource={smartOptionResource}
                deleteResource={api.settings.deleteSmartOption}
                isVisible={visibleModal === DELETE_MODAL}
                onClose={handleModalClose}
                onSubmit={onDelete}
            />
        </>
    );
};

export default SmartOptionControl;
